import React, { useState, useRef } from 'react'
import { Link } from 'wouter'
import { PlayIcon, PauseIcon, RotateCcwIcon, DownloadIcon } from 'lucide-react'

import { ShareModal } from './ShareModal'
import { ShareIcon, RotataCcwIcon } from 'lucide-react'

interface VideoPlayerProps {
  videoUrl: string
  requestId: string | null
  onRestart: () => void
  selectedAvatar?: {
    image: string
    name: string
  }
  user?: {
    authenticated: boolean
    username: string
    user_id: string
    user_credits: number
  }
  userCredits?: number
  resetAvatarBuilderState?: () => void
}

const APP_URL = import.meta.env.VITE_API_BASE_URL

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  onRestart,
  selectedAvatar,
  requestId,
  aspectRatio,
  user,
  userCredits,
  resetAvatarBuilderState,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  const handleReset = () => {
    resetAvatarBuilderState()
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const register = () => {
    const url = `${APP_URL}/users/register?request_id=${requestId}`

    // navigate to the registration page
    window.open(url, '_blank')
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="md:grid md:grid-cols-2">
        <div className="bg-black flex items-center justify-center p-4">
          <div className="w-full max-w-[300px]">
            <div className="aspect-[9/16] relative">
              {selectedAvatar && (
                <div className="absolute inset-0 z-0 opacity-20">
                  <img 
                    src={selectedAvatar.image} 
                    alt={selectedAvatar.name} 
                    className="w-full h-full object-cover"
                    style={{
                      filter: 'blur(10px)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </div>
              )}
              <video
                ref={videoRef}
                src={videoUrl}
                className="w-full h-full object-contain relative z-10"
                onEnded={() => setIsPlaying(false)}
              />
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent z-20">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay();
                    }}
                    className="p-3 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                    aria-label={isPlaying ? 'Pause video' : 'Play video'}
                  >
                    {isPlaying ? (
                      <PauseIcon size={24} />
                    ) : (
                      <PlayIcon size={24} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Your video is ready!
            </h2>
            <p className="text-gray-600 mb-4 text-sm">
              Join our service to download and share this video.
            </p>
            <ul className="text-sm text-gray-600 space-y-1.5 mb-4">
              <li className="flex items-center gap-2">
                <span className="w-1 h-1 bg-purple-500 rounded-full"></span>
                Download your videos in high quality
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1 h-1 bg-purple-500 rounded-full"></span>
                Share your videos with a unique link
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="px-4 py-2 rounded-lg bg-white ring-1 ring-purple-500 text-purple-700 font-medium hover:bg-purple-100 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <ShareIcon size={18} />
                Share &amp; Download
              </button>

              <Link
                onClick={handleReset}
                className="px-4 py-2 rounded-lg bg-purple-500 ring-1 ring-purple-500 text-white font-medium hover:bg-purple-600 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <RotateCcwIcon size={18} />
                Create another video
              </Link>
            </div>
          </div>
        </div>
      </div>
      <ShareModal
        isOpen={isShareModalOpen}
        user={user}
        userCredits={userCredits}
        requestId={requestId}
        aspectRatio={aspectRatio}
        onClose={() => setIsShareModalOpen(false)}
        videoUrl={videoUrl}
      />
    </div>
  )
}
